// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getHomePageUrl, getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { xScreen } from "@tps/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// <xng-change>
const enum fields {
    continueShoppingLink = "continueShoppingLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.continueShoppingLink]: LinkFieldValue;
    };
}
// </xng-change>

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCartState(state, state.pages.orderConfirmation.cartId),
    settingsCollection: getSettingsCollection(state),
    homePageUrl: getHomePageUrl(state),
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat, // ! Check whether this one is necessary, or the other vat variable below
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    // XNG-Change
    language: state.context.session.language,
    allProductsPageLink: getPageLinkByPageType(state, "AllProductsPage"),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

export interface OrderConfirmationHeaderStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    continueButton?: ButtonPresentationProps;
    printButton?: ButtonPresentationProps;
    addToListButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    addToListClickable?: ClickableProps;
    continueClickable?: ClickableProps;
    printListModal?: TwoButtonModalStyles;
    // XNG-Change
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    orderNumberHeading?: TypographyPresentationProps;
    orderNumberText?: TypographyPresentationProps;
    customMsg?: TypographyPresentationProps;
    creditHoldWarning?: TypographyPresentationProps;
}

export const headerStyles: OrderConfirmationHeaderStyles = {
    continueButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    addToListButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [2, 2, 2, 6, 6],
    },
    titleGridItem: {
        width: [10, 10, 10, 6, 6],
    },
    title: {
        variant: "h2",
        as: "p",
        css: css`
            margin: 0;

            @media print {
                font-size: 11px;
            }
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

type Props = HasHistory &
    OwnProps &
    ReturnType<typeof mapStateToProps> & {
        setAddToListModalIsOpen: (parameter: {
            productInfos?: Omit<ProductInfo, "productDetailPath">[];
            modalIsOpen: boolean;
        }) => void;
        addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    };

const styles = headerStyles;
const OrderConfirmationHeader: FC<Props> = props => {
    const { cartState } = props;
    const { history } = props;
    const {
        homePageUrl,
        // xng-change
        fields: { continueShoppingLink },
        allProductsPageLink,
    } = props;

    const toasterContext = React.useContext(ToasterContext);
    // xng-change
    const linkInfo = useGetLink(continueShoppingLink);

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    if (!cartState.value) {
        return null;
    }

    const continueClickHandler = () => {
        // xng-change
        const redirectLink = linkInfo?.url || allProductsPageLink?.url;

        if (redirectLink) {
            history.push(redirectLink);
        }
    };

    const addToListHandler = () => {
        if (!props.wishListSettings || !props?.cartState?.value?.cartLines) {
            return;
        }

        const productInfos = props?.cartState?.value?.cartLines.map(productInfo => ({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
        })) as Omit<ProductInfo, "productDetailPath">[];

        if (!props.wishListSettings.allowMultipleWishLists) {
            props.addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        props.setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    const printLabel = translate("Print Order");
    const continueLabel = translate("Continue Shopping");
    const addToListLabel = translate("Add to List");

    // Xng-Changes: TPS-628
    const erpOrders = cartState.value.properties?.erpOrderNumbers
        ? JSON.parse(cartState.value.properties?.erpOrderNumbers)
        : "";
    const erpNumbers = erpOrders?.Orders?.map((o: any) => o["Order-no"]);
    // Xng-Changes: TPS-938
    // const hasErpErrors = erpOrders?.errors !== null;
    // Xng-Changes: TPS-1152
    const hasErpErrors = !!erpNumbers?.some((o: any) => o?.toUpperCase()?.startsWith("E"))
    // Xng-Changes: TPS-970
    const ordersWithCreditHold = erpOrders?.Orders?.filter((order: any) => order.CreditHold?.toUpperCase() === "FAIL") || [];
    // Xng-Changes: TPS-1152
    const ordersWithCreditHoldError = erpOrders?.Orders?.some((order: any) => order.CreditHold?.toUpperCase() === "ERROR");

const hasMultipleOrders = erpOrders?.Orders?.length > 1;
    // XNG-Change: some changes on structure to match design anf FRD
    return (
        <StyledWrapper {...styles.headerGridContainer}>
            <Typography {...styles.title}>{siteMessage("OrderConfirmation_Success")}</Typography>
            <Typography {...styles.orderNumberHeading}>
                {translate("Order")}&nbsp;
                {/* Xng-Changes: TPS-628 & TPS-938 & TPS-1152 */}
                {(!hasErpErrors && !ordersWithCreditHoldError)
                    ?  erpNumbers
                    ? erpNumbers.map((orderNumber: string) => `#${orderNumber}`).join(", ")
                    : `#${cartState.value.erpOrderNumber || cartState.value.orderNumber}`
                    : siteMessage("Custom_OrderConfirmation_Pending")
                    }
            </Typography>
            {/* Xng-Changes: TPS-970 */}
            {ordersWithCreditHold.length > 0 && (
                <Typography {...styles.creditHoldWarning}>
                    {hasMultipleOrders
                        ? siteMessage("Custom_Credit_Hold_Warning_Multiple_Warehouses")
                        : siteMessage("Custom_Credit_Hold_Warning")}
                </Typography>
            )}
            <SmallHeadingAndText
                extendedStyles={styles.headingAndTextStyles}
                heading={translate("Order Date")}
                text={getLocalizedDateTime({
                    dateTime: new Date(cartState.value.orderDate as Date),
                    language: props.language,
                })}
            />
            <Typography {...styles.customMsg}>{siteMessage("Custom_OrderConfirmation_Msg")}</Typography>

            <StyledWrapper {...styles.buttonGridItem}>
                <Button {...styles.printButton} onClick={printOrOpenPrintAllModal}>
                    {printLabel}
                </Button>
                <Button {...styles.continueButton} onClick={continueClickHandler}>
                    {continueLabel}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderConfirmationHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
        // xng-change
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.continueShoppingLink,
                displayName: "Continue Shopping Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
